import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FormConfig } from '../models/dynamic.model';
import { CommonService, ValidFieldValue } from './common.service';
import {
  downloadInfoPageSectionKeys,
  regexPattern,
  validationErrorMessages,
} from '../utility/constants';
import { ValidatorPattern } from '../utility/validators.pattern';

type InputRestrictionTransform = (value: unknown, field: any) => unknown;

interface InputRestrictionHandlerEntry {
  matches: (form: FormGroup, field: any) => boolean;
  setup?: (form: FormGroup, field: any) => void;
  transform?: InputRestrictionTransform;
}

@Injectable({
  providedIn: 'root',
})
export class ValidatorService {
  constructor(
    private readonly fb: FormBuilder,
    private readonly commonService: CommonService
  ) {}

  private subscribeControlValueChanges(
    control: AbstractControl | null | undefined,
    destroyRef: DestroyRef,
    callback: (value: any) => void
  ): void {
    if (!control) {
      return;
    }
    control.valueChanges
      .pipe(takeUntilDestroyed(destroyRef))
      .subscribe(callback);
  }

  public generateForm(jsonConfig: any, destroyRef: DestroyRef): {
    form: FormGroup;
    errorMessages: any;
    options: any;
  } {
    const form = this.createForm(jsonConfig);
    const errorMessages = this.setErrorMessageObject(jsonConfig);
    const options = this.getSelectOptions(jsonConfig);
    this.handleRestriction(form, jsonConfig, destroyRef);
    this.noInitialWhiteSpace(form, jsonConfig, destroyRef);
    return { form, errorMessages, options };
  }

  /**
   * Typed counterpart of `generateForm`: one control per key of `defaults`, non-nullable, so
   * `reset()` returns every control to its default. Adds the start/end date order check when
   * both `startDate` and `endDate` exist, as `createForm` does.
   */
  public generateTypedForm<T extends object>(
    defaults: T,
    options: { required?: (keyof T)[] } = {}
  ): FormGroup<{ [K in keyof T]: FormControl<T[K]> }> {
    const controls = {} as { [K in keyof T]: FormControl<T[K]> };
    (Object.keys(defaults) as (keyof T)[]).forEach(key => {
      controls[key] = this.fb.nonNullable.control(
        defaults[key],
        options.required?.includes(key) ? Validators.required : null
      );
    });
    const group = new FormGroup(controls);
    if ('startDate' in defaults && 'endDate' in defaults) {
      group.setValidators(
        this.startDateBeforeEndDateValidator('startDate', 'endDate')
      );
    }
    return group;
  }

  public createForm(formConfig: FormConfig): FormGroup {
    const formGroup: any = {};
    for (const field of formConfig.fields) {
      const validators = this.buildValidators(field);

      formGroup[field.id] = [
        field.isDisableField ? { value: '', disabled: true } : '',
        validators,
      ];
    }

    const group = this.fb.group(formGroup);
    const groupValidators: ValidatorFn[] = [];
    const hasStartEnd =
      formConfig.fields.some(f => f.id === 'startDate') &&
      formConfig.fields.some(f => f.id === 'endDate');
    if (hasStartEnd) {
      groupValidators.push(
        this.startDateBeforeEndDateValidator('startDate', 'endDate')
      );
    }
    const hasPublishDateRange =
      formConfig.fields.some(f => f.id === 'publishDateFrom') &&
      formConfig.fields.some(f => f.id === 'publishDateTo');
    if (hasPublishDateRange) {
      groupValidators.push(
        this.startDateBeforeEndDateValidator(
          'publishDateFrom',
          'publishDateTo'
        )
      );
    }
    if (groupValidators.length) {
      group.setValidators(groupValidators);
    }

    return group;
  }

  private buildValidators(field: any): any[] {
    const validators = [];

    if (field.required) {
      validators.push(Validators.required);
    }
    if (field.minValue) {
      validators.push(Validators.minLength(field.minValue));
    }
    if (field.pattern) {
      validators.push(Validators.pattern(field.pattern));
    }
    if (field.maxLength) {
      validators.push(Validators.maxLength(field.maxLength));
    }
    if (field.isDecimalField) {
      validators.push(this.decimalValidator(3));
    }
    if (field.isHsCode) {
      validators.push(this.hsCodeValidator());
    }
    return validators;
  }

  public getErrorMessage(
    form: FormGroup,
    errorMessages: any,
    fieldId: string
  ): string {
    const control = form.get(fieldId);
    const fieldErrorMessages = errorMessages[fieldId];
    if (control?.errors && fieldErrorMessages) {
      const errorKey = Object.keys(control.errors)[0];
      if (errorKey === 'dateOrderInvalid') {
        return validationErrorMessages.dateOrderInvalid;
      }
      if (errorKey === 'notExist') {
        return (
          fieldErrorMessages['notExist'] || 'MAWB does not exist in the system'
        );
      }
      return fieldErrorMessages[errorKey] ?? '';
    }
    return '';
  }

  private setErrorMessageObject(formConfig: any): any {
    const errorMessages: any = {};
    for (const field of formConfig.fields) {
      if (
        field.requiredError ||
        field.patternError ||
        field.minError ||
        field.emailPatternError ||
        field.patternError ||
        field.phonePatternError ||
        field.maxLengthError ||
        field.minLengthError ||
        ''
      ) {
        errorMessages[field.id] = {
          required: field.requiredError || '',
          pattern:
            field.emailPatternError ||
            field.patternError ||
            field.phonePatternError ||
            '',
          minlength: field.minError || field.minLengthError || '',
          maxlength: field.maxLengthError || '',
          decimalInvalid: field.patternError || '',
        };
        if (field.id === downloadInfoPageSectionKeys.hsCode) {
          errorMessages[field.id]['invalidHSCode'] = '';
        }
        if (field.id === 'mawbExt') {
          errorMessages[field.id]['notExist'] =
            'MAWB does not exist in the system';
        }
      }
    }
    return errorMessages;
  }

  private getSelectOptions(jsonConfig: any): {
    selectOptions: any[];
    radioOptions: any[];
  } {
    const selectField = jsonConfig.fields.find(
      (field: any) => field.type === 'select'
    );
    const radioField = jsonConfig.fields.find(
      (field: any) => field.type === 'radio'
    );
    const selectOptions = selectField ? selectField.options : [];
    const radioOptions = radioField ? radioField.options : [];
    return { selectOptions, radioOptions };
  }

  private handleRestriction(
    form: FormGroup,
    formConfig: any,
    destroyRef: DestroyRef
  ) {
    for (const field of formConfig.fields) {
      this.applyInputRestrictionForField(form, field, destroyRef);
      this.applyDecimalFieldSanitizationIfNeeded(form, field, destroyRef);
    }
  }

  private applyInputRestrictionForField(
    form: FormGroup,
    field: any,
    destroyRef: DestroyRef
  ): void {
    const control = form.get(field.id);
    if (!control) {
      return;
    }

    const handlers = this.getInputRestrictionHandlers().filter(h =>
      h.matches(form, field)
    );

    for (const h of handlers) {
      h.setup?.(form, field);
    }

    const transforms = handlers
      .map(h => h.transform)
      .filter((t): t is InputRestrictionTransform => !!t);

    if (!transforms.length) {
      return;
    }

    this.subscribeControlValueChanges(control, destroyRef, value => {
      let next = value;
      for (const t of transforms) {
        next = t(next, field);
      }
      if (next !== value) {
        control.setValue(next, { emitEvent: false });
      }
    });
  }

  private getInputRestrictionHandlers(): InputRestrictionHandlerEntry[] {
    return [
      {
        matches: (_form, f) => !!f.allowNumeric,
        transform: (v, f) => this.transformAllowNumeric(v, f),
      },
      {
        matches: (_form, f) => !!f.allowNumericWithDecimal,
        transform: (v, f) => this.transformNumericWithDecimal(v, f),
      },
      {
        matches: (_form, f) => !!f.allowAlphabet,
        transform: (v, f) => this.transformAllowAlphabet(v, f),
      },
      {
        matches: (_form, f) => !!f.allowAlphaNumeric,
        transform: (v, f) => this.transformAllowAlphaNumeric(v, f),
      },
      {
        matches: (_form, f) => !!f.allowAlphabetWithSpace,
        transform: (v, f) => this.transformAlphabetWithSpace(v, f),
      },
      {
        matches: (_form, f) =>
          !!f.allowAlphaNumericWithDotUnderscoreDashSpecialCharacters,
        transform: (v, f) =>
          this.transformAlphaNumericWithDotUnderscoreDash(v, f),
      },
      {
        matches: (_form, f) =>
          !!f.allowAlphaNumericWithUnderscoreOnlySpecialCharacters,
        transform: (v, f) =>
          this.transformAlphaNumericWithUnderscoreOnly(v, f),
      },
      {
        matches: (_form, f) => !!f.allowAlphabetWithHyphenSpace,
        transform: (v, f) => this.transformAlphabetWithHyphenSpace(v, f),
      },
      {
        matches: (_form, f) =>
          !!f.allowAlphaNumericWithHyphenAmpersandParenthesesSpace,
        transform: (v, f) =>
          this.transformAlphaNumericWithHyphenAmpersandParenthesesSpace(
            v,
            f
          ),
      },
      {
        matches: (_form, f) =>
          !!f.allowAlphaNumericWithSpaceNoSpecialCharacter,
        transform: (v, f) =>
          this.transformAlphaNumericWithSpaceNoSpecialCharacter(v, f),
      },
      {
        matches: (form, f) => !!form.get(f.id) && !!f.isCheckForMaxValue,
        transform: (v, f) => this.transformMaxValueLength(v, f),
      },
      {
        matches: (_form, f) => !!f.allowNumberWithFirstNonZeroNumber,
        transform: (v, f) => this.transformNumberWithFirstNonZero(v, f),
      },
      {
        matches: (_form, f) => !!f.allowPaymentTermsInput,
        setup: (form, field) =>
          this.applyPaymentTermsValidators(form, field),
      },
    ];
  }

  private transformAllowNumeric(value: unknown, field: any): unknown {
    if (
      typeof value !== 'string' ||
      !this.commonService.isValidField(value)
    ) {
      return value;
    }
    const sanitizedValue = value.replaceAll(/\D/g, '');
    const trimmedValue = sanitizedValue.slice(0, field.maxValue);
    if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
      return trimmedValue;
    }
    return value;
  }

  private transformAllowAlphabet(value: unknown, field: any): unknown {
    const v = value as ValidFieldValue;
    if (!this.commonService.isValidField(v)) {
      return value;
    }
    const sanitizedValue = String(v).replaceAll(/[^a-z]/gi, '');
    const trimmedValue = sanitizedValue.slice(0, field.maxValue);
    if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
      return trimmedValue;
    }
    return value;
  }

  private transformAllowAlphaNumeric(value: unknown, field: any): unknown {
    if (
      typeof value !== 'string' ||
      !this.commonService.isValidField(value)
    ) {
      return value;
    }
    const sanitizedValue = value.replaceAll(/[^a-z0-9]/gi, '');
    const trimmedValue = sanitizedValue.slice(0, field.maxValue);
    if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
      return trimmedValue;
    }
    return value;
  }

  private transformAlphabetWithSpace(value: unknown, field: any): unknown {
    const v = value as ValidFieldValue;
    if (!this.commonService.isValidField(v)) {
      return value;
    }
    if (v !== ' ') {
      const sanitizedValue = String(v).replaceAll(/[^a-z ]/gi, '');
      const trimmedValue = sanitizedValue.slice(0, field.maxValue);
      if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
        return trimmedValue;
      }
      return value;
    }
    const trimmedValue =
      typeof v === 'string' ? v.trimStart() : (v as any)?.trimStart?.();
    if (value !== trimmedValue) {
      return trimmedValue;
    }
    return value;
  }

  private transformAlphaNumericWithDotUnderscoreDash(
    value: unknown,
    field: any
  ): unknown {
    if (!this.commonService.isValidField(value as ValidFieldValue)) {
      return value;
    }
    const sanitizedValue = (value as string).replaceAll(/[^a-z0-9._-]/gi, '');
    const trimmedValue = sanitizedValue.slice(0, field.maxValue);
    if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
      return trimmedValue;
    }
    return value;
  }

  private transformAlphaNumericWithUnderscoreOnly(
    value: unknown,
    field: any
  ): unknown {
    if (!this.commonService.isValidField(value as ValidFieldValue)) {
      return value;
    }
    const sanitizedValue = (value as string).replaceAll(/[^a-z0-9_]/gi, '');
    const trimmedValue = sanitizedValue.slice(0, field.maxValue);
    if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
      return trimmedValue;
    }
    return value;
  }

  private transformAlphabetWithHyphenSpace(value: unknown, field: any): unknown {
    if (!this.commonService.isValidField(value as ValidFieldValue)) {
      return value;
    }
    const sanitizedValue = (value as string).replaceAll(/[^a-zA-Z -]/g, '');
    const trimmedValue = sanitizedValue.slice(0, field.maxValue);
    if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
      return trimmedValue;
    }
    return value;
  }

  private transformAlphaNumericWithHyphenAmpersandParenthesesSpace(
    value: unknown,
    field: any
  ): unknown {
    if (!this.commonService.isValidField(value as ValidFieldValue)) {
      return value;
    }
    const sanitizedValue = (value as string).replaceAll(
      /[^a-zA-Z0-9 \-&()]/g,
      ''
    );
    const trimmedValue = sanitizedValue.slice(0, field.maxValue);
    if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
      return trimmedValue;
    }
    return value;
  }

  private transformAlphaNumericWithSpaceNoSpecialCharacter(
    value: unknown,
    field: any
  ): unknown {
    if (!this.commonService.isValidField(value as ValidFieldValue)) {
      return value;
    }
    const sanitizedValue = (value as string).replaceAll(
      regexPattern.alphanumericWithSpace,
      ''
    );
    const trimmedValue = sanitizedValue.slice(0, field.maxValue);
    if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
      return trimmedValue;
    }
    return value;
  }

  private transformMaxValueLength(value: unknown, field: any): unknown {
    if (value === ' ') {
      return value;
    }
    const trimmedValue = `${((value ?? '') as ValidFieldValue) || ''}`.slice(
      0,
      field.maxValue
    );
    if (value !== trimmedValue) {
      return trimmedValue;
    }
    return value;
  }

  private transformNumberWithFirstNonZero(value: unknown, field: any): unknown {
    if (
      typeof value !== 'string' ||
      !this.commonService.isValidField(value)
    ) {
      return value;
    }
    let sanitizedValue = value.replaceAll(/\D/g, '');
    if (sanitizedValue.startsWith('0')) {
      sanitizedValue = sanitizedValue.replaceAll(/^0+/g, '');
    }
    const trimmedValue = sanitizedValue.slice(0, field.maxValue);
    if (value !== sanitizedValue || sanitizedValue !== trimmedValue) {
      return trimmedValue;
    }
    return value;
  }

  private transformNumericWithDecimal(value: unknown, _field: any): unknown {
    if (!this.isValidNumericValue(value)) {
      return value;
    }
    const sanitizedValue = this.sanitizeNumericWithDecimal(value as string);
    if (value !== sanitizedValue) {
      return sanitizedValue;
    }
    return value;
  }

  private applyPaymentTermsValidators(
    form: FormGroup,
    field: any
  ): void {
    const control = form.get(field.id);
    if (!control) {
      return;
    }
    const existingValidators = control.validator ? [control.validator] : [];
    control.setValidators([
      ...existingValidators,
      (c: any) => {
        const value = c.value;
        if (value && typeof value === 'string') {
          const isNumeric = /^\d+$/.test(value);
          if (isNumeric) {
            const numValue = parseInt(value, 10);
            if (numValue > field.maxValue) {
              return { maxValue: true };
            }
          }
        }
        return null;
      },
    ]);
  }

  private applyDecimalFieldSanitizationIfNeeded(
    form: FormGroup,
    field: any,
    destroyRef: DestroyRef
  ): void {
    if (!form.get(field.id) || !field.isDecimalField) {
      return;
    }
    const control = form.get(field.id);
    if (!control) {
      return;
    }
    this.subscribeControlValueChanges(control, destroyRef, value => {
      if (this.commonService.isValidField(value)) {
        let sanitized = value.replaceAll(/[^0-9.]/g, '');

        const parts = sanitized.split('.');
        if (parts.length > 2) {
          sanitized = parts[0] + '.' + parts[1];
        }

        if (parts[1]?.length > 3) {
          sanitized = parts[0] + '.' + parts[1].substring(0, 3);
        }

        sanitized = sanitized.slice(0, 10);

        if (value !== sanitized) {
          control.setValue(sanitized, { emitEvent: false });
        }
      }
    });
  }

  private noInitialWhiteSpace(
    form: FormGroup,
    formConfig: any,
    destroyRef: DestroyRef
  ) {
    for (const field of formConfig.fields) {
      if (field.noInitialWhiteSpace) {
        const control = form.get(field.id);
        if (control) {
          this.subscribeControlValueChanges(control, destroyRef, value => {
            if (typeof value === 'string') {
              // Check if value is a string
              const trimmedValue = value.trimStart(); // Remove initial white space
              if (value !== trimmedValue) {
                control.setValue(trimmedValue, { emitEvent: false });
              }
            }
          });
        }
      }
    }
  }

  private isValidNumericValue(value: unknown): boolean {
    return typeof value === 'string' && this.commonService.isValidField(value);
  }

  private sanitizeNumericWithDecimal(value: string): string {
    let sanitizedValue = this.removeInvalidCharacters(value);
    sanitizedValue = this.handleNegativeSign(sanitizedValue, value);
    sanitizedValue = this.handleMultipleDecimals(sanitizedValue);
    sanitizedValue = this.limitIntegerPart(sanitizedValue);
    sanitizedValue = this.limitDecimalPlaces(sanitizedValue);
    sanitizedValue = this.handleLeadingDot(sanitizedValue);
    return sanitizedValue;
  }

  private removeInvalidCharacters(value: string): string {
    return value.replaceAll(/[^-0-9.]/g, '');
  }

  private handleNegativeSign(
    sanitizedValue: string,
    originalValue: string
  ): string {
    const hasNegative = originalValue.startsWith('-');

    if (hasNegative && !sanitizedValue.startsWith('-')) {
      return '-' + sanitizedValue.replaceAll('-', '');
    }

    if (!hasNegative) {
      return sanitizedValue.replaceAll('-', '');
    }

    return sanitizedValue;
  }

  private handleMultipleDecimals(sanitizedValue: string): string {
    const parts = sanitizedValue.split('.');
    if (parts.length > 2) {
      return parts[0] + '.' + parts.slice(1).join('');
    }
    return sanitizedValue;
  }

  private limitIntegerPart(sanitizedValue: string): string {
    const parts = sanitizedValue.split('.');
    const integerPart = parts[0].replaceAll('-', '');

    if (integerPart.length <= 10) {
      return sanitizedValue;
    }

    const negative = parts[0].startsWith('-') ? '-' : '';
    return (
      negative +
      integerPart.substring(0, 10) +
      (parts[1] === undefined ? '' : '.' + parts[1])
    );
  }

  private limitDecimalPlaces(sanitizedValue: string): string {
    const parts = sanitizedValue.split('.');
    if (parts[1] && parts[1].length > 2) {
      return parts[0] + '.' + parts[1].substring(0, 2);
    }
    return sanitizedValue;
  }

  private handleLeadingDot(sanitizedValue: string): string {
    if (sanitizedValue.startsWith('.')) {
      return '0' + sanitizedValue;
    }
    return sanitizedValue;
  }

  public startDateBeforeEndDateValidator(
    startKey: string,
    endKey: string,
    options?: { deferRequiredPair?: boolean }
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const startControl = group.get(startKey);
      const endControl = group.get(endKey);

      const start = startControl?.value;
      const end = endControl?.value;

      // Validation 2: If end date is filled but start date is empty, show error on start date
      // (skipped when the caller shows the required error only on a search attempt)
      if (!start && end && !options?.deferRequiredPair) {
        startControl?.setErrors({ startDateRequired: true });
        startControl?.markAsTouched(); // Mark as touched to show error immediately
        // Clear any previous errors on end date
        if (endControl?.hasError('dateOrderInvalid')) {
          endControl.setErrors(null);
          endControl.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false,
          });
        }
        return { startDateRequired: true };
      }

      // Clear startDateRequired error if start date is now filled
      if (start && startControl?.hasError('startDateRequired')) {
        startControl.setErrors(null);
        startControl.updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        });
      }

      // Only validate date order when both dates are present
      if (!start || !end) {
        if (!start && !end && startControl?.hasError('startDateRequired')) {
          startControl.setErrors(null);
          startControl.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false,
          });
        }
        // Clear custom errors if previously set
        if (endControl?.hasError('dateOrderInvalid')) {
          endControl.setErrors(null);
          endControl.updateValueAndValidity({
            onlySelf: true,
            emitEvent: false,
          });
        }
        return null;
      }

      const startDate = new Date(start);
      const endDate = new Date(end);

      // Validation 1: Start date cannot be greater than end date
      if (startDate > endDate) {
        endControl?.setErrors({ dateOrderInvalid: true });
        endControl?.markAsTouched(); // Mark as touched to show error immediately
        return { dateOrderInvalid: true };
      }

      // Clear the error if dates are valid
      if (endControl?.hasError('dateOrderInvalid')) {
        endControl.setErrors(null);
        endControl.updateValueAndValidity({ onlySelf: true, emitEvent: false });
      }

      return null;
    };
  }
  public decimalValidator(maxDecimalPlaces: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === '') return null;
      const regex = new RegExp(`^\\d+(\\.\\d{1,${maxDecimalPlaces}})?$`);
      return regex.test(value) ? null : { decimalInvalid: true };
    };
  }

  public hsCodeValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;
      if (value === null || value === '' || value === undefined) {
        return null;
      }
      const stringValue = String(value).trim();
      return /^[0-9]{6,10}$/.test(stringValue) ? null : { invalidHSCode: true };
    };
  }

  public validateFlightNumberControl(
    control: AbstractControl | null | undefined,
    options?: {
      allowValues?: string[];
      skipWhenDisabled?: boolean;
    }
  ): boolean {
    const {
      allowValues = [],
      skipWhenDisabled = true,
    } = options ?? {};

    if (!control || (skipWhenDisabled && control.disabled)) {
      return true;
    }

    const flightNumber = `${control.value ?? ''}`.trim();
    const existingErrors = control.errors ?? {};

    if (!flightNumber || allowValues.includes(flightNumber)) {
      if (existingErrors['invalidFlightPattern']) {
        const { invalidFlightPattern, ...restErrors } = existingErrors;
        control.setErrors(Object.keys(restErrors).length ? restErrors : null);
      }
      return true;
    }

    const isValidPattern =
      ValidatorPattern.FlightNumberAlphaNumericPrefixDigits.test(flightNumber);
    if (!isValidPattern) {
      control.setErrors({ ...existingErrors, invalidFlightPattern: true });
      return false;
    }

    if (existingErrors['invalidFlightPattern']) {
      const { invalidFlightPattern, ...restErrors } = existingErrors;
      control.setErrors(Object.keys(restErrors).length ? restErrors : null);
    }
    return true;
  }
}
